import type { TaskState } from '@/components/tasks/constants';

export type BulkEditableDeadline = {
  id: string;
  state: TaskState;
  created_by?: 'auto' | 'manual' | number | null;
  assigned_to_guid?: string | null;
};

export type BulkDeadlineStatusTarget = 'unchanged' | TaskState;
export type BulkDeadlineAssigneeTarget = 'unchanged' | 'unassigned' | string;

export type BulkDeadlineChanges = {
  state?: TaskState;
  stateTaskGuids: string[];
  assignedToGuid?: string | null;
  assigneeTaskGuids: string[];
  skippedManualStateGuids: string[];
  changedDeadlineIds: string[];
};

export function parseBulkDeadlineAssigneeTarget(value: string): BulkDeadlineAssigneeTarget {
  if (value === 'unchanged' || value === 'unassigned') {
    return value;
  }

  if (!value) {
    throw new Error(`Invalid deadline assignee target: ${value}`);
  }

  return value;
}

export function isUserCreatedDeadline(deadline: Pick<BulkEditableDeadline, 'created_by'>): boolean {
  return Boolean(deadline.created_by);
}

export function calculateBulkDeadlineChanges(
  deadlines: BulkEditableDeadline[],
  statusTarget: BulkDeadlineStatusTarget,
  assigneeTarget: BulkDeadlineAssigneeTarget,
): BulkDeadlineChanges {
  const state = statusTarget === 'unchanged' ? undefined : statusTarget;
  const assignedToGuid =
    assigneeTarget === 'unchanged'
      ? undefined
      : assigneeTarget === 'unassigned'
        ? null
        : assigneeTarget;

  const skippedManualStateGuids = state === 'Extracted'
    ? deadlines
      .filter((deadline) => deadline.state !== state && isUserCreatedDeadline(deadline))
      .map((deadline) => deadline.id)
    : [];

  const stateTaskGuids = state
    ? deadlines
      .filter((deadline) => deadline.state !== state)
      .filter((deadline) => state !== 'Extracted' || !isUserCreatedDeadline(deadline))
      .map((deadline) => deadline.id)
    : [];

  const assigneeTaskGuids = assigneeTarget !== 'unchanged'
    ? deadlines
      .filter((deadline) => (deadline.assigned_to_guid ?? null) !== assignedToGuid)
      .map((deadline) => deadline.id)
    : [];

  return {
    state,
    stateTaskGuids,
    assignedToGuid,
    assigneeTaskGuids,
    skippedManualStateGuids,
    changedDeadlineIds: Array.from(new Set([...stateTaskGuids, ...assigneeTaskGuids])),
  };
}
